import React, { useState, useEffect, useRef } from "react";
import { CheckIcon, XMarkIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import type { ObjectiveEditorProps } from "../objectivesPhase.types";
import { Button } from "@/components";

export default function ObjectiveEditor({
  objective,
  index,
  onUpdate,
  onDelete,
  isEditing,
  onEdit,
  onSave,
  onCancel,
}: ObjectiveEditorProps) {
  const [editValue, setEditValue] = useState(objective);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [editValue, isEditing]);

  // Focus when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [isEditing]);

  // Reset edit value when objective changes
  useEffect(() => {
    setEditValue(objective);
  }, [objective]);

  const handleSave = () => {
    onUpdate(index, editValue.trim());
    onSave(index);
  };

  const handleCancel = () => {
    setEditValue(objective);
    onCancel(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div className="bg-white border border-raro-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors group">
      <div className="flex items-start space-x-3">
        {/* Index Number */}
        <div className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-sm font-medium mt-1">
          {index + 1}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-3">
              <textarea
                ref={textareaRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Descreva um objetivo SMART para este projeto..."
                className="w-full resize-none border border-raro-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={2}
              />
              <div className="flex items-center justify-between">
                <div className="text-xs text-raro-gray-500">
                  Ctrl + Enter para salvar • Esc para cancelar
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                    size="sm"
                  >
                    <XMarkIcon className="w-4 h-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!editValue.trim()}
                    className="bg-green-600 text-white hover:bg-green-700"
                    size="sm"
                  >
                    <CheckIcon className="w-4 h-4 mr-1" />
                    Salvar
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-raro-gray-900 leading-relaxed">
                {objective || (
                  <span className="text-raro-gray-500 italic">
                    Clique para adicionar um objetivo...
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        {!isEditing && (
          <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex items-center space-x-1">
              <button
                onClick={() => onEdit(index)}
                className="p-1.5 text-raro-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded"
                title="Editar objetivo"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(index)}
                className="p-1.5 text-raro-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                title="Excluir objetivo"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SMART Objective Hint */}
      {isEditing && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800 font-medium mb-1">💡 Dica: Objetivos SMART</p>
          <div className="text-xs text-blue-700 space-y-1">
            <div><strong>S</strong>pecífico • <strong>M</strong>ensurável • <strong>A</strong>tingível • <strong>R</strong>elevante • <strong>T</strong>emporal</div>
          </div>
        </div>
      )}
    </div>
  );
}